import { DependencyList, SyntheticEvent, useCallback, useRef } from 'react';
import { ClickPosition } from './click-position';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { useAtomValue } from 'jotai';
import { CurrentTrafficUtil, EPageType } from 'shared/models';
import { $SEOCurrentTraffic, $SEOEnterTraffic } from 'shared/atoms/common/traffic';

interface ReflowClickConfig<T = Element, E = Event> {
  /**
   * 默认行为
   */
  defaultBehavior?: (event?: SyntheticEvent<T, E>) => void;
  /**
   * 触发弹窗行为
   */
  modalBehavior?: (event?: SyntheticEvent<T, E>) => void;
  /**
   * 强制导流时的行为
   */
  reflowBehavior?: (event?: SyntheticEvent<T, E>) => void;
  /**
   * 最终都会走到的一个方法
   */
  finallyBehavior?: (event?: SyntheticEvent<T, E>) => void;
  /**
   * 点击事件上报埋点
   */
  clickEvent?: (event?: SyntheticEvent<T, E>, bundle?: Record<string, keyof any>) => Record<string, keyof any>;
}

/**
 * 封装强制导流功能的点击事件
 */
export default function useReflowClick<T = Element, E = Event>(config: ReflowClickConfig<T, E>, deps: DependencyList) {
  const { isMobile, isBot, isInApp } = useAtomValue($UserAgent);
  const { trafficPid: trafficType } = useAtomValue($SEOEnterTraffic); // ssr traffic data
  const traffic = useAtomValue($SEOCurrentTraffic); // csr traffic data
  const { pageType } = traffic;
  const pageId = CurrentTrafficUtil.getPageIdFromTraffic(traffic);

  const isRealUser = !isBot && isMobile && !isInApp;

  const shouldReflow = isRealUser || (isMobile && pageType === EPageType.discover);

  const notReflow = useRef<boolean>(false);

  return useCallback(
    (event?: SyntheticEvent<T, E>) => {
      const { defaultBehavior, modalBehavior, reflowBehavior, finallyBehavior, clickEvent } = config;

      let isCTA = 1;
      let forceReflow = false;
      if (clickEvent) {
        const eventParams = clickEvent(event);

        const { current_position = '', click_result = '' } = eventParams;
        // 避免discover页弹窗not now也reflow
        if (current_position === ClickPosition.first_landing_dialog && click_result === 'cancel') {
          notReflow.current = true;
          isCTA = 0;
        }

        if (current_position === ClickPosition.comment_image) {
          forceReflow = true;
        }

        window.Tea('wap_click', {
          ...eventParams,
          is_cta: isCTA,
        });
      }

      if (isRealUser && modalBehavior) {
        event?.preventDefault();
        event?.stopPropagation();
        modalBehavior(event);
      } else if ((shouldReflow || forceReflow) && !notReflow.current && reflowBehavior) {
        event?.preventDefault();
        event?.stopPropagation();
        reflowBehavior(event);
      } else {
        defaultBehavior?.(event);
      }

      finallyBehavior?.(event);
    },
    [config, isMobile, shouldReflow, pageType, trafficType, pageId, notReflow, ...deps],
  );
}
